import { brokersService } from '@/services/brokers';
import type { Broker } from '@/services/brokers/types';
import { useQuery } from '@tanstack/react-query';

/**
 * Valida se o corretor persistido no contexto ainda existe para o usuário atual.
 *
 * A checagem é feita por consulta direcionada ao código do corretor, e não
 * varrendo a página da listagem: o search index do InsureMO corta em 1000
 * registros e não garante ordenação, então "não está na página" nunca pode ser
 * lido como "não existe".
 *
 * `isMissing` só fica true quando a consulta respondeu com sucesso e mesmo
 * assim não trouxe o corretor. Enquanto carrega, ou em qualquer falha de
 * request, retorna false — perder a seleção por erro de rede seria pior do que
 * manter uma seleção obsoleta.
 */
export function useValidateSelectedBroker(broker: Broker | null) {
  const code = broker?.code;

  const { data, isSuccess } = useQuery({
    queryKey: ['brokers', 'validate', code],
    queryFn: async () => {
      if (!code) throw new Error('Código do corretor não informado');

      const response = await brokersService.listBrokers({
        search: code,
        pageSize: 5,
      });

      if (!response.success || !response.data) {
        throw new Error(response.error ?? 'Erro ao validar corretor');
      }

      return response.data.data.some(item => item.code === code);
    },
    enabled: Boolean(code),
    staleTime: 1000 * 60 * 5,
  });

  return { isMissing: isSuccess && data === false };
}
